import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { CustomTooltip } from "./tooltip";

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground transition-colors duration-200 hover:bg-secondary hover:text-white",
        destructive:
          "bg-destructive/80 text-white shadow-sm hover:bg-destructive/50 active:scale-98 transition-all duration-200 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline:
          "border border-primary text-primary bg-background shadow-xs hover:bg-primary hover:text-accent-foreground dark:bg-input/30 dark:border-primary dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:!bg-gray-600/10",
        ghostPrimary:
          " transition-all text-primary hover:bg-primary/10 hover:text-primary",
        link: "text-primary underline-offset-4 hover:underline",
        "outline-warning":
          "border border-[#FFB800] text-[#FFB800] bg-background shadow-xs hover:bg-[#FFB800] hover:text-[#FFB800] dark:bg-input/30 dark:border-[#FFB800] dark:hover:bg-input/50 hover:text-white",
        outlineDestructive:
          "border border-destructive text-destructive hover:bg-destructive/10 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:border-destructive",
        warning: "bg-yellow-300 hover:opacity-50",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    tooltip?: string;
  }) {
  const Comp = asChild ? Slot : "button";
  const { tooltip, ...buttonProps } =
    props as React.ComponentProps<"button"> & { tooltip?: string };

  const buttonElement = (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...buttonProps}
    />
  );

  if (tooltip) {
    return <CustomTooltip content={tooltip}>{buttonElement}</CustomTooltip>;
  }

  return buttonElement;
}

export { Button, buttonVariants };
